import { useEffect, useMemo, useState } from "react";
import { useEscapeKey } from "../../../hooks/useEscapeKey";
import { AnimatePresence, motion } from "framer-motion";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InsightsIcon from "@mui/icons-material/Insights";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CircularProgress from "@mui/material/CircularProgress";
import type { ClientLogo } from "./clientLogo";
import {
  CAMPOS,
  DOCUMENTOS,
  CAMPO_API_MAP,
  DOCUMENTO_API_MAP,
  QRLinkCard,
  addStep,
  OPCION_CAMPOS,
  SeleccionState,
  buildInitialSeleccion,
  parseSeleccionResponse,
} from "./CrearFormularioModal";
import MetricasFormularioModal, {
  DemoDataBanner,
  MetricsKPIRow,
  MetricsTimelineChart,
  MetricsFunnel,
  useFormMetrics,
} from "./MetricasFormularioModal";

// ─── types ────────────────────────────────────────────────────────────────────

interface IFormularioApi {
  contextFormId: string;
  formType: string;
  client: string;
  businessAreaId: string;
  vacancyId: string;
  recruiterId: string;
  recruiter: string;
  link: string;
  totalRecords: number;
  totalApplications: number;
  steps: number;
  createdAt: string;
  status?: string | null;
}

interface IFieldConfigApi {
  step: "E1" | "E2";
  field: string;
  visible: boolean;
  required: boolean;
}

interface IFormConfigApi {
  configType?: "COMPLETO" | "PASOS";
  fields?: IFieldConfigApi[];
  formDescription?: string | null;
  jobOfferDescription?: string | null;
  clientLogo?: ClientLogo | null;
}

interface IFormHistoryEntry {
  id: string;
  contextFormId: string;
  action: string;
  summary: string;
  details: string[];
  userId: string | null;
  userName: string | null;
  createdAt: string;
}

interface VerFormularioModalProps {
  formulario: IFormularioApi;
  onClose: () => void;
}

// ─── reverse maps ─────────────────────────────────────────────────────────────

const API_TO_CAMPO_KEY: Record<string, string> = Object.entries(CAMPO_API_MAP)
  .filter(([, v]) => v)
  .reduce<Record<string, string>>((acc, [k, v]) => {
    acc[v as string] = k;
    return acc;
  }, {});

const API_TO_DOC_KEY: Record<string, string> = Object.entries(DOCUMENTO_API_MAP).reduce<
  Record<string, string>
>((acc, [k, v]) => {
  acc[v] = k;
  return acc;
}, {});

const CAMPO_LABEL_BY_KEY: Record<string, string> = [...CAMPOS, ...DOCUMENTOS].reduce<
  Record<string, string>
>((acc, c) => {
  acc[c.key] = c.label;
  return acc;
}, {});

// ─── presentation config ──────────────────────────────────────────────────────

const TIPO_CONFIG: Record<string, { bg: string; color: string; label: string }> = {
  CLIENTE:    { bg: "#ede9fe", color: "#6d28d9", label: "Cliente"    },
  CUENTA:     { bg: "#dbeafe", color: "#1d4ed8", label: "Cuenta"     },
  RECLUTADOR: { bg: "#fef3c7", color: "#b45309", label: "Reclutador" },
};

const ESTADO_CONFIG: Record<string, { label: string; bg: string; color: string; dot: string }> = {
  ACTIVA:           { label: "Activo",           bg: "#d1fae5", color: "#047857", dot: "#10b981" },
  INACTIVO:         { label: "Inactivo",         bg: "#fee2e2", color: "#b91c1c", dot: "#ef4444" },
  VACANTE_INACTIVA: { label: "Vacante inactiva", bg: "#fef3c7", color: "#b45309", dot: "#f59e0b" },
};

const resolveEstado = (status: string | null | undefined) => {
  if (!status) return ESTADO_CONFIG.ACTIVA;
  const key = status.trim().toUpperCase();
  return ESTADO_CONFIG[key] ?? ESTADO_CONFIG.ACTIVA;
};

const formatDate = (iso: string) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const dd   = String(d.getUTCDate()).padStart(2, "0");
  const mm   = String(d.getUTCMonth() + 1).padStart(2, "0");
  const yyyy = d.getUTCFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const formatDateTime = (iso: string) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const dd   = String(d.getUTCDate()).padStart(2, "0");
  const mm   = String(d.getUTCMonth() + 1).padStart(2, "0");
  const yyyy = d.getUTCFullYear();
  const hh   = String(d.getUTCHours()).padStart(2, "0");
  const min  = String(d.getUTCMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} · ${hh}:${min}`;
};

// Configuración visual por tipo de evento de la bitácora.
const ACCION_CONFIG: Record<string, { label: string; color: string; bg: string; icon: string }> = {
  CREACION:             { label: "Creación",      color: "#047857", bg: "#d1fae5", icon: "✚" },
  CONFIGURACION_CAMPOS: { label: "Campos",        color: "#1d4ed8", bg: "#dbeafe", icon: "☑" },
  DESCRIPCIONES:        { label: "Descripciones", color: "#b45309", bg: "#fef3c7", icon: "✎" },
  LOGO_CLIENTE:         { label: "Logo cliente", color: "#0369a1", bg: "#e0f2fe", icon: "▧" },
  ESTADO:               { label: "Estado",        color: "#6d28d9", bg: "#ede9fe", icon: "⏻" },
  OPCIONES:             { label: "Opciones",      color: "#0e7490", bg: "#cffafe", icon: "≡" },
};

const resolveAccion = (action: string) =>
  ACCION_CONFIG[action] ?? { label: action || "Cambio", color: "#475569", bg: "#f1f5f9", icon: "•" };

// ─── shared building blocks ───────────────────────────────────────────────────

const InfoField = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
    <span
      style={{
        fontFamily: "Roboto, sans-serif",
        fontSize: "10.5px",
        fontWeight: 600,
        color: "#94a3b8",
        textTransform: "uppercase",
        letterSpacing: "0.07em",
      }}
    >
      {label}
    </span>
    <span
      style={{
        fontFamily: "Roboto, sans-serif",
        fontSize: "13px",
        color: "#1e293b",
        fontWeight: 500,
      }}
    >
      {value}
    </span>
  </div>
);

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      fontFamily: "Roboto, sans-serif",
      fontSize: "11px",
      fontWeight: 700,
      color: "#475569",
      textTransform: "uppercase",
      letterSpacing: "0.1em",
      marginBottom: "10px",
      display: "flex",
      alignItems: "center",
      gap: "10px",
    }}
  >
    <span>{children}</span>
    <div style={{ flex: 1, height: "1px", background: "#f1f5f9" }} />
  </div>
);

// ─── field list (per step) ────────────────────────────────────────────────────

interface FieldEntry {
  key: string;
  label: string;
  required: boolean;
}

const FieldList = ({
  title,
  entries,
}: {
  title: string;
  entries: FieldEntry[];
}) => {
  if (entries.length === 0) {
    return (
      <div style={{ marginBottom: "12px" }}>
        <div
          style={{
            fontFamily: "Roboto, sans-serif",
            fontSize: "11.5px",
            fontWeight: 600,
            color: "#64748b",
            marginBottom: "6px",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontFamily: "Roboto, sans-serif",
            fontSize: "12px",
            color: "#94a3b8",
            fontStyle: "italic",
            paddingLeft: "4px",
          }}
        >
          Sin {title.toLowerCase()} configurados en este paso.
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: "12px" }}>
      <div
        style={{
          fontFamily: "Roboto, sans-serif",
          fontSize: "11.5px",
          fontWeight: 600,
          color: "#64748b",
          marginBottom: "6px",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
        }}
      >
        {title}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
        {entries.map((entry) => (
          <div
            key={entry.key}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "7px 10px",
              borderRadius: "8px",
              background: "#fff",
              border: "1px solid #f1f5f9",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <CheckCircleIcon style={{ fontSize: 16, color: "#10b981" }} />
              <span
                style={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "13px",
                  color: "#1e293b",
                }}
              >
                {entry.label}
              </span>
            </div>
            <span
              style={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "10.5px",
                fontWeight: 600,
                color: entry.required ? "#b45309" : "#94a3b8",
                background: entry.required ? "rgba(180,83,9,0.1)" : "#f1f5f9",
                padding: "3px 9px",
                borderRadius: "10px",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                whiteSpace: "nowrap",
              }}
            >
              {entry.required ? "Obligatorio" : "Opcional"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── historial / bitácora ─────────────────────────────────────────────────────

const HistorialPanel = ({ contextFormId }: { contextFormId: string }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [entries, setEntries] = useState<IFormHistoryEntry[]>([]);

  useEffect(() => {
    let cancelled = false;
    const baseUrl = process.env.REACT_APP_API_APPLICANT;
    if (!baseUrl) {
      setError("Configuración faltante: REACT_APP_API_APPLICANT");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    fetch(`${baseUrl}/form/history/${encodeURIComponent(contextFormId)}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return (await res.json()) as { history?: IFormHistoryEntry[] };
      })
      .then((data) => {
        if (cancelled) return;
        setEntries(Array.isArray(data?.history) ? data.history : []);
      })
      .catch(() => { if (!cancelled) setError("No se pudo cargar el historial del formulario."); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [contextFormId]);

  if (loading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", padding: "48px 0" }}>
        <CircularProgress size={24} style={{ color: "#0065DD" }} />
        <span style={{ fontFamily: "Roboto, sans-serif", fontSize: "12.5px", color: "#64748b" }}>
          Cargando historial…
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          padding: "12px",
          borderRadius: "9px",
          background: "#fef2f2",
          border: "1px solid #fecaca",
          fontFamily: "Roboto, sans-serif",
          fontSize: "12.5px",
          color: "#b91c1c",
          textAlign: "center",
        }}
      >
        {error}
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "8px",
          padding: "48px 24px",
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: "30px", lineHeight: 1 }}>🗒️</div>
        <div style={{ fontFamily: "Roboto, sans-serif", fontSize: "14px", fontWeight: 600, color: "#334155" }}>
          Sin cambios registrados
        </div>
        <div style={{ fontFamily: "Roboto, sans-serif", fontSize: "12.5px", color: "#94a3b8", maxWidth: "360px" }}>
          Aún no hay eventos en la bitácora de este formulario. Los cambios que se hagan a partir de ahora quedarán registrados aquí.
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: "relative", paddingLeft: "6px" }}>
      {/* línea vertical del timeline */}
      <div
        style={{
          position: "absolute",
          left: "19px",
          top: "8px",
          bottom: "8px",
          width: "2px",
          background: "#e2e8f0",
        }}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        {entries.map((entry) => {
          const cfg = resolveAccion(entry.action);
          return (
            <div key={entry.id} style={{ position: "relative", display: "flex", gap: "14px" }}>
              {/* nodo / ícono */}
              <div
                style={{
                  flexShrink: 0,
                  width: "28px",
                  height: "28px",
                  borderRadius: "50%",
                  background: cfg.bg,
                  color: cfg.color,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "14px",
                  fontWeight: 700,
                  border: "2px solid #fff",
                  boxShadow: "0 0 0 1px #e2e8f0",
                  zIndex: 1,
                }}
              >
                {cfg.icon}
              </div>

              {/* tarjeta del evento */}
              <div
                style={{
                  flex: 1,
                  background: "#fff",
                  border: "1px solid #e2e8f0",
                  borderRadius: "10px",
                  padding: "12px 14px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "10px",
                    flexWrap: "wrap",
                    marginBottom: entry.details.length > 0 ? "8px" : 0,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                    <span
                      style={{
                        fontFamily: "Roboto, sans-serif",
                        fontSize: "9.5px",
                        fontWeight: 700,
                        color: cfg.color,
                        background: cfg.bg,
                        padding: "2px 8px",
                        borderRadius: "8px",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      {cfg.label}
                    </span>
                    <span style={{ fontFamily: "Roboto, sans-serif", fontSize: "13px", fontWeight: 600, color: "#1e293b" }}>
                      {entry.summary}
                    </span>
                  </div>
                  <span style={{ fontFamily: "Roboto, sans-serif", fontSize: "11px", color: "#94a3b8", whiteSpace: "nowrap" }}>
                    {formatDateTime(entry.createdAt)}
                  </span>
                </div>

                {/* autor */}
                <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: entry.details.length > 0 ? "8px" : 0 }}>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "18px",
                      height: "18px",
                      borderRadius: "50%",
                      background: "rgba(0,101,221,0.1)",
                      color: "#0065DD",
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "9px",
                      fontWeight: 700,
                      textTransform: "uppercase",
                    }}
                  >
                    {(entry.userName ?? "?").trim().charAt(0) || "?"}
                  </span>
                  <span style={{ fontFamily: "Roboto, sans-serif", fontSize: "12px", color: "#64748b" }}>
                    {entry.userName?.trim() || "Usuario del sistema"}
                  </span>
                </div>

                {/* detalles */}
                {entry.details.length > 0 && (
                  <ul style={{ margin: 0, paddingLeft: "18px", display: "flex", flexDirection: "column", gap: "3px" }}>
                    {entry.details.map((line, idx) => (
                      <li
                        key={idx}
                        style={{
                          fontFamily: "Roboto, sans-serif",
                          fontSize: "12.5px",
                          color: "#475569",
                          lineHeight: "1.5",
                        }}
                      >
                        {line}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── component ────────────────────────────────────────────────────────────────

const VerFormularioModal = ({ formulario, onClose }: VerFormularioModalProps) => {
  // Esc cierra el modal.
  useEscapeKey(onClose);
  const tipoCfg   = TIPO_CONFIG[formulario.formType] ?? {
    bg: "#f1f5f9", color: "#475569", label: formulario.formType || "—",
  };
  const estadoCfg = resolveEstado(formulario.status);
  const isDividido = (formulario.steps ?? 1) >= 2;

  const [loadingConfig, setLoadingConfig] = useState(true);
  const [loadError, setLoadError]         = useState<string | null>(null);
  const [config, setConfig]               = useState<IFormConfigApi | null>(null);
  const [opciones, setOpciones]           = useState<SeleccionState>(buildInitialSeleccion());
  const [showFullMetrics, setShowFullMetrics] = useState(false);
  const [descFormOpen, setDescFormOpen]   = useState(false);
  const [descOfertaOpen, setDescOfertaOpen] = useState(false);
  const [activeTab, setActiveTab]         = useState<"detalle" | "historial">("detalle");

  const metrics = useFormMetrics(formulario, 30);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const baseUrl = process.env.REACT_APP_API_APPLICANT;
    if (!baseUrl) {
      setLoadError("Configuración faltante: REACT_APP_API_APPLICANT");
      setLoadingConfig(false);
      return;
    }
    const userId = sessionStorage.getItem("usuarioId") ?? "";
    const url =
      `${baseUrl}/form/config/${encodeURIComponent(formulario.contextFormId)}` +
      `?userId=${encodeURIComponent(userId)}`;

    const optionsUrl = `${baseUrl}/form/options/${encodeURIComponent(formulario.contextFormId)}`;

    Promise.all([
      fetch(url, { method: "GET", headers: { "Content-Type": "application/json" } }).then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return (await res.json()) as IFormConfigApi;
      }),
      fetch(optionsUrl, { method: "GET", headers: { "Content-Type": "application/json" } })
        .then(async (res) => (res.ok ? await res.json() : {}))
        .catch(() => ({})),
    ])
      .then(([configData, optionsData]) => {
        if (cancelled) return;
        setConfig(configData ?? {});
        setOpciones(parseSeleccionResponse(optionsData));
      })
      .catch(() => { if (!cancelled) setLoadError("No se pudo cargar la configuración del formulario."); })
      .finally(() => { if (!cancelled) setLoadingConfig(false); });

    return () => { cancelled = true; };
  }, [formulario.contextFormId]);

  const { campos1, docs1, campos2, docs2 } = useMemo(() => {
    const c1: FieldEntry[] = [];
    const d1: FieldEntry[] = [];
    const c2: FieldEntry[] = [];
    const d2: FieldEntry[] = [];

    (config?.fields ?? []).forEach((f) => {
      if (!f.visible) return;
      const campoKey = API_TO_CAMPO_KEY[f.field];
      const docKey   = API_TO_DOC_KEY[f.field];
      const item = campoKey
        ? { key: campoKey, label: CAMPO_LABEL_BY_KEY[campoKey] ?? campoKey, required: !!f.required }
        : docKey
          ? { key: docKey, label: CAMPO_LABEL_BY_KEY[docKey] ?? docKey, required: !!f.required }
          : null;
      if (!item) return;
      const targetCampos = f.step === "E2" ? c2 : c1;
      const targetDocs   = f.step === "E2" ? d2 : d1;
      if (campoKey) targetCampos.push(item);
      else          targetDocs.push(item);
    });

    return { campos1: c1, docs1: d1, campos2: c2, docs2: d2 };
  }, [config]);

  const modoLabel = isDividido ? "Formulario por pasos" : "Formulario completo";

  const links = useMemo(() => {
    if (!formulario.link) return [];
    const built = [addStep(formulario.link, 1)];
    if (isDividido) built.push(addStep(formulario.link, 2));
    return built;
  }, [formulario.link, isDividido]);

  // Build context info to show
  const infoFields: { label: string; value: React.ReactNode }[] = [];
  if (formulario.formType === "CLIENTE" || formulario.formType === "CUENTA") {
    infoFields.push({ label: "Cliente", value: formulario.client || "—" });
  }
  if (formulario.formType === "CUENTA") {
    infoFields.push({ label: "Área de negocio", value: formulario.businessAreaId || "—" });
  }
  if (formulario.formType === "RECLUTADOR") {
    infoFields.push({ label: "Reclutador", value: formulario.recruiter || formulario.recruiterId || "—" });
    infoFields.push({ label: "Vacante", value: formulario.vacancyId || "—" });
  }
  infoFields.push({
    label: "Postulaciones",
    value: (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: "32px",
          padding: "2px 10px",
          borderRadius: "12px",
          background: (formulario.totalApplications ?? 0) > 0 ? "rgba(0,101,221,0.08)" : "#f1f5f9",
          color: (formulario.totalApplications ?? 0) > 0 ? "#0065DD" : "#94a3b8",
          fontFamily: "Roboto, sans-serif",
          fontSize: "12.5px",
          fontWeight: 600,
          border: (formulario.totalApplications ?? 0) > 0 ? "1px solid rgba(0,101,221,0.18)" : "1px solid #e2e8f0",
        }}
      >
        {formulario.totalApplications ?? 0}
      </span>
    ),
  });
  infoFields.push({
    label: "Estado",
    value: (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          padding: "3px 10px",
          borderRadius: "14px",
          background: estadoCfg.bg,
          color: estadoCfg.color,
          fontSize: "11.5px",
          fontWeight: 600,
          fontFamily: "Roboto, sans-serif",
          whiteSpace: "nowrap",
          border: `1px solid ${estadoCfg.color}22`,
        }}
      >
        <span
          style={{
            display: "inline-block",
            width: "7px",
            height: "7px",
            borderRadius: "50%",
            background: estadoCfg.dot,
            boxShadow: `0 0 4px ${estadoCfg.dot}88`,
            marginRight: "5px",
            flexShrink: 0,
          }}
        />
        {estadoCfg.label}
      </span>
    ),
  });
  infoFields.push({ label: "Fecha de creación", value: formatDate(formulario.createdAt) });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.55)",
        backdropFilter: "blur(5px)",
        WebkitBackdropFilter: "blur(5px)",
        zIndex: 1200,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "32px 24px 48px",
        overflowY: "auto",
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 28, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 18, scale: 0.97 }}
        transition={{ type: "spring", stiffness: 300, damping: 26 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          width: "100%",
          maxWidth: isDividido ? "880px" : "820px",
          borderRadius: "18px",
          overflow: "hidden",
          background: "#fff",
          boxShadow: "0 28px 64px rgba(15,23,42,0.28), 0 0 0 1px rgba(15,23,42,0.07)",
        }}
      >
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

        {/* ── Header ── */}
        <div
          style={{
            background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)",
            padding: "22px 24px",
            position: "relative",
          }}
        >
          <button
            onClick={onClose}
            style={{
              position: "absolute", top: "14px", right: "14px",
              width: "28px", height: "28px", borderRadius: "50%",
              border: "none", background: "transparent",
              cursor: "pointer", color: "rgba(255,255,255,0.6)",
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: 0, lineHeight: 1, outline: "none",
              transition: "color 0.15s",
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#fff"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "rgba(255,255,255,0.6)"; }}
          >
            <CloseIcon style={{ fontSize: 16 }} />
          </button>
          <div style={{ fontFamily: "Roboto, sans-serif", fontSize: "16px", fontWeight: 600, color: "#fff" }}>
            Detalle del formulario
          </div>
          <div style={{ fontFamily: "Roboto, sans-serif", fontSize: "13px", color: "rgba(255,255,255,0.5)", marginTop: "3px" }}>
            Configuración, campos y enlaces de acceso
          </div>
        </div>

        {/* ── Tabs ── */}
        <div
          style={{
            display: "flex",
            gap: "4px",
            padding: "0 24px",
            background: "#fff",
            borderBottom: "1px solid #f1f5f9",
          }}
        >
          {([
            { key: "detalle", label: "Detalle" },
            { key: "historial", label: "Historial / Bitácora" },
          ] as { key: "detalle" | "historial"; label: string }[])
            .map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  position: "relative",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  padding: "13px 10px",
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "13px",
                  fontWeight: isActive ? 600 : 500,
                  color: isActive ? "#0065DD" : "#64748b",
                  outline: "none",
                  transition: "color 0.15s",
                }}
                onMouseEnter={(e) => { if (!isActive) (e.currentTarget as HTMLButtonElement).style.color = "#334155"; }}
                onMouseLeave={(e) => { if (!isActive) (e.currentTarget as HTMLButtonElement).style.color = "#64748b"; }}
              >
                {tab.label}
                {isActive && (
                  <motion.div
                    layoutId="ver-form-tab-underline"
                    style={{
                      position: "absolute",
                      left: "8px",
                      right: "8px",
                      bottom: "-1px",
                      height: "2.5px",
                      borderRadius: "2px",
                      background: "#0065DD",
                    }}
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* ── Body ── */}
        <div style={{ padding: "24px", maxHeight: "66vh", overflowY: "auto" }}>

          {activeTab === "historial" ? (
            <HistorialPanel contextFormId={formulario.contextFormId} />
          ) : (
          <>

          {/* Tipo chip */}
          <div style={{ marginBottom: "16px" }}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "4px 12px",
                borderRadius: "14px",
                background: tipoCfg.bg,
                color: tipoCfg.color,
                fontSize: "11.5px",
                fontWeight: 600,
                fontFamily: "Roboto, sans-serif",
                whiteSpace: "nowrap",
              }}
            >
              {tipoCfg.label}
            </span>
          </div>

          {/* Info grid */}
          <SectionLabel>Información</SectionLabel>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: "16px 24px",
              padding: "16px 18px",
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              marginBottom: "22px",
            }}
          >
            {infoFields.map((f) => (
              <InfoField key={f.label} label={f.label} value={f.value} />
            ))}
          </div>

          {/* Descripciones (solo si al menos una existe) */}
          {!loadingConfig && !loadError && config?.clientLogo && (
            <section style={{ marginBottom: 22 }}>
              <SectionLabel>Identidad visual del cliente</SectionLabel>
              <div style={{ border: "1px solid #e2e8f0", borderRadius: 12, padding: 16, background: "#f8fafc" }}>
                <span style={{ fontSize: 12, color: config.clientLogo.enabled ? "#047857" : "#64748b" }}>
                  {config.clientLogo.enabled ? "Visible para postulantes" : "Desactivado · No se muestra al postulante"}
                </span>
                <span style={{ fontSize: 12, color: "#64748b", marginLeft: 10 }}>
                  · {config.clientLogo.placement === "banner" ? "Banner superior" : "Dentro de la tarjeta"}
                </span>
                <div style={{ background: "repeating-conic-gradient(#e2e8f0 0% 25%, white 0% 50%) 0 0 / 20px 20px", borderRadius: 8, marginTop: 12 }}>
                  <img src={config.clientLogo.image} alt={`Logo de ${formulario.client}`}
                    style={{ display: "block", width: "100%", maxWidth: config.clientLogo.placement === "banner" ? 640 : 320, height: "auto", margin: "0 auto" }} />
                </div>
                <p style={{ fontSize: 11, color: "#64748b", margin: "8px 0 0", overflowWrap: "anywhere" }}>Escritorio/tablet · {config.clientLogo.fileName}</p>
                {config.clientLogo.mobile && <>
                  <div style={{ background: "repeating-conic-gradient(#e2e8f0 0% 25%, white 0% 50%) 0 0 / 20px 20px", borderRadius: 8, marginTop: 12 }}>
                    <img src={config.clientLogo.mobile.image} alt={`Versión móvil de ${formulario.client}`}
                      style={{ display: "block", width: "100%", maxWidth: 320, height: "auto", margin: "0 auto" }} />
                  </div>
                  <p style={{ fontSize: 11, color: "#64748b", margin: "8px 0 0", overflowWrap: "anywhere" }}>Móvil · {config.clientLogo.mobile.fileName}</p>
                </>}
                <p style={{ fontSize: 11, color: "#64748b", margin: "8px 0 0" }}>La cuadrícula indica transparencia; no forma parte de las imágenes.</p>
              </div>
            </section>
          )}

          {!loadingConfig && !loadError && (config?.formDescription || config?.jobOfferDescription) && (
            <>
              <SectionLabel>Descripciones</SectionLabel>
              <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "22px" }}>

                {/* Descripción del formulario */}
                {config?.formDescription && (
                  <div
                    style={{
                      background: "#f8fafc",
                      border: "1px solid #e2e8f0",
                      borderLeft: "3px solid #f59e0b",
                      borderRadius: "10px",
                      overflow: "hidden",
                    }}
                  >
                    {/* Header clicable */}
                    <button
                      onClick={() => setDescFormOpen((v) => !v)}
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "11px 14px",
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        textAlign: "left",
                        transition: "background 0.15s",
                      }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(245,158,11,0.06)"; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
                    >
                      <span
                        style={{
                          fontFamily: "Roboto, sans-serif",
                          fontSize: "10.5px",
                          fontWeight: 700,
                          color: "#b45309",
                          background: "rgba(245,158,11,0.12)",
                          padding: "2px 9px",
                          borderRadius: "8px",
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          flexShrink: 0,
                        }}
                      >
                        📋 Descripción del formulario
                      </span>
                      <span
                        style={{
                          fontFamily: "Roboto, sans-serif",
                          fontSize: "10px",
                          color: "#94a3b8",
                          fontStyle: "italic",
                          flex: 1,
                        }}
                      >
                        Uso interno
                      </span>
                      {/* Chevron */}
                      <svg
                        width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                        style={{ flexShrink: 0, transition: "transform 0.28s cubic-bezier(0.4,0,0.2,1)", transform: descFormOpen ? "rotate(180deg)" : "rotate(0deg)" }}
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>
                    {/* Contenido animado */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateRows: descFormOpen ? "1fr" : "0fr",
                        transition: "grid-template-rows 0.3s cubic-bezier(0.4,0,0.2,1)",
                      }}
                    >
                      <div style={{ overflow: "hidden" }}>
                        <p
                          style={{
                            fontFamily: "Roboto, sans-serif",
                            fontSize: "13px",
                            color: "#334155",
                            margin: 0,
                            lineHeight: "1.6",
                            whiteSpace: "pre-wrap",
                            padding: "0 14px 13px",
                            borderTop: "1px solid rgba(245,158,11,0.15)",
                            paddingTop: "10px",
                          }}
                        >
                          {config.formDescription}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Descripción de la oferta laboral */}
                {config?.jobOfferDescription && (
                  <div
                    style={{
                      background: "#f8fafc",
                      border: "1px solid #e2e8f0",
                      borderLeft: "3px solid #0065DD",
                      borderRadius: "10px",
                      overflow: "hidden",
                    }}
                  >
                    {/* Header clicable */}
                    <button
                      onClick={() => setDescOfertaOpen((v) => !v)}
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "11px 14px",
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        textAlign: "left",
                        transition: "background 0.15s",
                      }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(0,101,221,0.05)"; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
                    >
                      <span
                        style={{
                          fontFamily: "Roboto, sans-serif",
                          fontSize: "10.5px",
                          fontWeight: 700,
                          color: "#0065DD",
                          background: "rgba(0,101,221,0.1)",
                          padding: "2px 9px",
                          borderRadius: "8px",
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          flexShrink: 0,
                        }}
                      >
                        💼 Descripción de la oferta
                      </span>
                      <span
                        style={{
                          fontFamily: "Roboto, sans-serif",
                          fontSize: "10px",
                          color: "#10b981",
                          fontStyle: "italic",
                          fontWeight: 500,
                          flex: 1,
                        }}
                      >
                        Visible para postulantes
                      </span>
                      {/* Chevron */}
                      <svg
                        width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0065DD" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                        style={{ flexShrink: 0, transition: "transform 0.28s cubic-bezier(0.4,0,0.2,1)", transform: descOfertaOpen ? "rotate(180deg)" : "rotate(0deg)" }}
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>
                    {/* Contenido animado */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateRows: descOfertaOpen ? "1fr" : "0fr",
                        transition: "grid-template-rows 0.3s cubic-bezier(0.4,0,0.2,1)",
                      }}
                    >
                      <div style={{ overflow: "hidden" }}>
                        <div style={{ borderTop: "1px solid rgba(0,101,221,0.12)", padding: "10px 14px 13px" }}>
                          <div
                            className="ver-form-offer-body"
                            dangerouslySetInnerHTML={{ __html: config.jobOfferDescription }}
                            style={{
                              fontFamily: "Roboto, sans-serif",
                              fontSize: "13px",
                              color: "#334155",
                              lineHeight: "1.6",
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <style>{`
                  .ver-form-offer-body p { margin: 0 0 6px 0; }
                  .ver-form-offer-body p:last-child { margin-bottom: 0; }
                  .ver-form-offer-body h1 { font-size: 1.1rem; font-weight: 700; margin: 8px 0 4px; color: #0f172a; }
                  .ver-form-offer-body h2 { font-size: 1rem; font-weight: 700; margin: 8px 0 4px; color: #0f172a; }
                  .ver-form-offer-body h3 { font-size: 0.93rem; font-weight: 700; margin: 6px 0 4px; color: #0f172a; }
                  .ver-form-offer-body ul { list-style: disc; padding-left: 20px; margin: 4px 0; }
                  .ver-form-offer-body ol { list-style: decimal; padding-left: 20px; margin: 4px 0; }
                  .ver-form-offer-body li { margin: 2px 0; }
                  .ver-form-offer-body strong, .ver-form-offer-body b { font-weight: 600; }
                  .ver-form-offer-body em, .ver-form-offer-body i { font-style: italic; }
                  .ver-form-offer-body u { text-decoration: underline; }
                `}</style>
              </div>
            </>
          )}

          {/* Configuración */}
          <SectionLabel>Configuración del formulario</SectionLabel>
          <div
            style={{
              padding: "16px 18px",
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              marginBottom: "22px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "14px" }}>
              <span
                style={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "10.5px",
                  fontWeight: 600,
                  color: "#94a3b8",
                  textTransform: "uppercase",
                  letterSpacing: "0.07em",
                }}
              >
                Modo
              </span>
              <span
                style={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#0065DD",
                  background: "rgba(0,101,221,0.08)",
                  padding: "3px 12px",
                  borderRadius: "12px",
                  border: "1px solid rgba(0,101,221,0.18)",
                }}
              >
                {modoLabel}
              </span>
            </div>

            {loadingConfig ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", padding: "32px 0" }}>
                <CircularProgress size={24} style={{ color: "#0065DD" }} />
                <span style={{ fontFamily: "Roboto, sans-serif", fontSize: "12.5px", color: "#64748b" }}>
                  Cargando configuración…
                </span>
              </div>
            ) : loadError ? (
              <div
                style={{
                  padding: "12px",
                  borderRadius: "9px",
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "12.5px",
                  color: "#b91c1c",
                  textAlign: "center",
                }}
              >
                {loadError}
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {/* Paso 1 (siempre) */}
                <div
                  style={{
                    background: "#fff",
                    border: "1px solid #e2e8f0",
                    borderRadius: "10px",
                    padding: "14px",
                  }}
                >
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "3px 12px",
                      borderRadius: "12px",
                      background: "rgba(0,101,221,0.09)",
                      color: "#0065DD",
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "11px",
                      fontWeight: 700,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      marginBottom: "12px",
                    }}
                  >
                    {isDividido ? "Paso 1" : "Campos del formulario"}
                  </div>
                  <FieldList title="Campos" entries={campos1} />
                  <FieldList title="Documentos" entries={docs1} />
                </div>

                {/* Paso 2 (solo dividido) */}
                {isDividido && (
                  <div
                    style={{
                      background: "#fff",
                      border: "1px solid #e2e8f0",
                      borderRadius: "10px",
                      padding: "14px",
                    }}
                  >
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        padding: "3px 12px",
                        borderRadius: "12px",
                        background: "rgba(0,101,221,0.09)",
                        color: "#0065DD",
                        fontFamily: "Roboto, sans-serif",
                        fontSize: "11px",
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        marginBottom: "12px",
                      }}
                    >
                      Paso 2
                    </div>
                    <FieldList title="Campos" entries={campos2} />
                    <FieldList title="Documentos" entries={docs2} />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Opciones de campos (turno / cargo) */}
          {!loadingConfig && !loadError && OPCION_CAMPOS.some((k) => opciones[k].length > 0) && (
            <>
              <SectionLabel>Opciones de campos</SectionLabel>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: "16px",
                  marginBottom: "22px",
                }}
              >
                {OPCION_CAMPOS.filter((k) => opciones[k].length > 0).map((k) => (
                  <div
                    key={k}
                    style={{
                      background: "#f8fafc",
                      border: "1px solid #e2e8f0",
                      borderRadius: "12px",
                      padding: "14px 16px",
                    }}
                  >
                    <div
                      style={{
                        fontFamily: "Roboto, sans-serif",
                        fontSize: "11.5px",
                        fontWeight: 600,
                        color: "#64748b",
                        marginBottom: "9px",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      {CAMPO_LABEL_BY_KEY[k] ?? k}
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                      {opciones[k].map((o, idx) => (
                        <span
                          key={`${o.value}-${idx}`}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "4px 11px",
                            borderRadius: "999px",
                            background: "#fff",
                            border: "1px solid rgba(0,101,221,0.25)",
                            color: "#0050b3",
                            fontFamily: "Roboto, sans-serif",
                            fontSize: "12.5px",
                            fontWeight: 500,
                          }}
                        >
                          {o.value}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* TODO: Sección métricas oculta temporalmente — ver PENDIENTE_METRICAS.md */}

          {/* Enlaces / QR */}
          {links.length > 0 && (
            <>
              <SectionLabel>{isDividido ? "Enlaces de acceso" : "Enlace de acceso"}</SectionLabel>
              <div
                style={{
                  display: "flex",
                  gap: "16px",
                  width: "100%",
                  flexDirection: isDividido ? "row" : "column",
                  alignItems: isDividido ? "stretch" : "center",
                }}
              >
                {links.map((url, i) => (
                  <QRLinkCard
                    key={url}
                    url={url}
                    stepLabel={isDividido ? `Paso ${i + 1}` : undefined}
                    readOnly
                  />
                ))}
              </div>
            </>
          )}
          </>
          )}
        </div>

        {/* ── Footer ── */}
        <div
          style={{
            padding: "16px 24px",
            borderTop: "1px solid #f1f5f9",
            display: "flex",
            justifyContent: "flex-end",
            background: "#fafafa",
          }}
        >
          <motion.button
            onClick={onClose}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            style={{
              padding: "8px 22px",
              borderRadius: "9px",
              border: "1px solid #e2e8f0",
              background: "#fff",
              color: "#475569",
              fontFamily: "Roboto, sans-serif",
              fontSize: "13px",
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Cerrar
          </motion.button>
        </div>
      </motion.div>

      {/* TODO: MetricasFormularioModal oculto temporalmente — ver PENDIENTE_METRICAS.md */}
    </motion.div>
  );
};

export default VerFormularioModal;
